import { ref, onBeforeUnmount } from 'vue'
import { useEventListener } from '@vueuse/core'
import { PANEL_CONFIG } from './usePanels'

export function useWindowCentering() {
  // State management
  const resizeTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const scrollStopTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const isScrolling = ref(false)
  const isTransitioning = ref(false)
  
  // Throttling state for scroll handler
  let scrollFrameId: number | null = null
  
  // DOM measurement cache
  const domCache = ref({
    windowHeight: window.innerHeight,
    panelTabRect: null as DOMRect | null,
    lastCacheUpdate: 0,
    isValid: false
  })
  
  // Panel tab reference (to be set by parent component)
  let panelTabRef: HTMLElement | null = null
  
  // Set the panel tab reference
  function setPanelTabRef(element: HTMLElement | null) {
    panelTabRef = element
  }
  
  // Update cached DOM measurements
  function updateDOMCache() {
    const now = Date.now()
    
    // Only update cache if it's stale
    if (domCache.value.isValid && (now - domCache.value.lastCacheUpdate) < PANEL_CONFIG.CACHE_DURATION) {
      return domCache.value
    }
    
    domCache.value.windowHeight = window.innerHeight
    
    if (panelTabRef) {
      domCache.value.panelTabRect = panelTabRef.getBoundingClientRect()
    }
    
    domCache.value.lastCacheUpdate = now
    domCache.value.isValid = true
    
    return domCache.value
  }
  
  // Invalidate cache (called on resize)
  function invalidateDOMCache() {
    domCache.value.isValid = false
    domCache.value.panelTabRect = null
  }
  
  // Helper function to set panel tab title opacity
  function setTabTitleOpacity(opacity: number) {
    if (!panelTabRef) return
    
    // CSS will handle mobile/reduced-motion checks
    panelTabRef.style.setProperty('--tab-title-opacity', opacity.toString())
  }
  
  // Update panel tab title position using cached measurements
  function updatePanelTabPosition() {
    if (!panelTabRef) return
    
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      if (!panelTabRef) return
      
      try {
        // Always get fresh measurements to be immune to panel transforms
        const panelTabRect = panelTabRef.getBoundingClientRect()
        const windowHeight = window.innerHeight
        
        // Calculate positions
        const panelTabCenter = panelTabRect.top + (panelTabRect.height / 2)
        const windowCenter = windowHeight / 2
        const offsetFromPanelCenter = windowCenter - panelTabCenter
        
        // Set CSS custom property for title positioning
        // CSS will handle mobile/reduced-motion logic
        panelTabRef.style.setProperty('--window-center-offset', `${offsetFromPanelCenter}px`)
      } catch (error) {
        console.warn('Panel tab positioning failed:', error)
        // Fallback: reset to center
        if (panelTabRef) {
          panelTabRef.style.setProperty('--window-center-offset', '0px')
        }
      }
    })
  }
  
  // Debounced resize handler for window center positioning
  function debouncedHandleResize() {
    if (resizeTimeout.value) {
      clearTimeout(resizeTimeout.value)
    }
    
    resizeTimeout.value = setTimeout(() => {
      invalidateDOMCache() // Invalidate cache on resize
      updatePanelTabPosition()
    }, PANEL_CONFIG.RESIZE_DEBOUNCE)
  }
  
  // Throttled scroll handler with immediate fade logic
  function handleScroll() {
    // Handle fade out/in behavior immediately (outside throttle to prevent lag)
    if (!isScrolling.value) {
      // Just started scrolling - fade out immediately
      isScrolling.value = true
      setTabTitleOpacity(.1)
    }
    
    // Clear any existing timeout
    if (scrollStopTimeout.value) {
      clearTimeout(scrollStopTimeout.value)
    }
    
    // Set new timeout to fade in after scroll stops
    scrollStopTimeout.value = setTimeout(() => {
      isScrolling.value = false
      setTabTitleOpacity(1)
      scrollStopTimeout.value = null
    }, PANEL_CONFIG.SCROLL_FADE_DELAY)
    
    // Throttle the position update using requestAnimationFrame
    // Gate with visibility state - stop when tab is backgrounded
    if (document.visibilityState === 'hidden') return
    
    if (scrollFrameId !== null) return // Already scheduled
    
    scrollFrameId = requestAnimationFrame(() => {
      updatePanelTabPosition()
      scrollFrameId = null
    })
  }
  
  // Simplified, robust position transition handler
  function handlePositionTransition(
    type: 'panel-open' | 'panel-close' | 'route-change',
    options: {
      onComplete?: () => void
      shouldInvalidateCache?: boolean
    } = {}
  ) {
    if (!panelTabRef) return

    // Invalidate cache if requested
    if (options.shouldInvalidateCache) {
      invalidateDOMCache()
    }

    // Quick and reliable: fade out → reposition → fade in
    setTabTitleOpacity(0)
    
    // Wait for panel transforms to settle, then update position
    const settlementDelay = type === 'route-change' ? 100 : 200
    
    setTimeout(() => {
      updatePanelTabPosition()
      
      // Fade back in after position is updated
      setTimeout(() => {
        setTabTitleOpacity(1)
        options.onComplete?.()
      }, 150)
    }, settlementDelay)
  }
  
  // Initialize window centering
  function initializeWindowCentering() {
    // Small delay to ensure panel is fully positioned
    setTimeout(() => {
      invalidateDOMCache() // Start with fresh cache
      updatePanelTabPosition()
      setTabTitleOpacity(1) // Initialize as visible
    }, PANEL_CONFIG.OBSERVER_INIT_DELAY)
  }
  
  // Set up event listeners using VueUse for automatic cleanup
  useEventListener('resize', () => {
    // Invalidate cache on resize
    invalidateDOMCache()
    debouncedHandleResize()
  }, { passive: true })

  useEventListener('scroll', handleScroll, { passive: true })
  
  // Cleanup function
  function cleanup() {
    // Clear any pending timeouts and frame requests
    if (resizeTimeout.value) {
      clearTimeout(resizeTimeout.value)
      resizeTimeout.value = null
    }
    if (scrollStopTimeout.value) {
      clearTimeout(scrollStopTimeout.value)
      scrollStopTimeout.value = null
    }
    if (scrollFrameId !== null) {
      cancelAnimationFrame(scrollFrameId)
      scrollFrameId = null
    }
    
    // Clear DOM cache
    invalidateDOMCache()
    
    // Clear transitions
    if (isTransitioning.value) {
      isTransitioning.value = false
    }
  }
  
  // Cleanup on component unmount
  onBeforeUnmount(cleanup)
  
  return {
    // State
    isTransitioning,
    isScrolling,
    
    // Methods
    setPanelTabRef,
    initializeWindowCentering,
    handlePositionTransition,
    updatePanelTabPosition,
    setTabTitleOpacity,
    invalidateDOMCache,
    cleanup
  }
} 